/**
 * Centralized logging service for frontend
 * Replaces console.log/warn/error with structured logging
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: Record<string, unknown>;
  stack?: string;
}

class Logger {
  private minLevel: LogLevel = LogLevel.INFO;

  /**
   * Set the minimum log level
   */
  setLevel(level: LogLevel): void {
    this.minLevel = level;
  }

  /**
   * Should log based on level
   */
  private shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel;
  }

  /**
   * Format log entry
   */
  private formatEntry(level: LogLevel, message: string, context?: Record<string, unknown>): LogEntry {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      context,
    };

    if (level >= LogLevel.ERROR && Error().stack) {
      entry.stack = Error().stack;
    }

    return entry;
  }

  /**
   * Send log to external service (for production)
   */
  private async sendToService(entry: LogEntry): Promise<void> {
    // In production, send to logging service (e.g., Sentry, LogRocket)
    if (import.meta.env.PROD) {
      try {
        const baseUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://api.voni.dev';
        const apiUrl = baseUrl.endsWith('/api/v1') ? baseUrl : `${baseUrl}/api/v1`;

        await fetch(`${apiUrl}/logs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          },
          body: JSON.stringify(entry),
        });
      } catch (error) {
        // Silently fail if logging service is unavailable
      }
    }
  }

  /**
   * Debug logging
   */
  debug(message: string, context?: Record<string, unknown>): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;

    const entry = this.formatEntry(LogLevel.DEBUG, message, context);

    if (import.meta.env.DEV) {
      if (context) {
        console.debug(`[DEBUG] ${entry.timestamp}`, message, context);
      } else {
        console.debug(`[DEBUG] ${entry.timestamp}`, message);
      }
    }

    this.sendToService(entry).catch(() => { });
  }

  /**
   * Info logging
   */
  info(message: string, context?: Record<string, unknown>): void {
    if (!this.shouldLog(LogLevel.INFO)) return;

    const entry = this.formatEntry(LogLevel.INFO, message, context);

    if (import.meta.env.DEV) {
      if (context) {
        console.info(`[INFO] ${entry.timestamp}`, message, context);
      } else {
        console.info(`[INFO] ${entry.timestamp}`, message);
      }
    }

    this.sendToService(entry).catch(() => { });
  }

  /**
   * Warning logging
   */
  warn(message: string, context?: Record<string, unknown>): void {
    if (!this.shouldLog(LogLevel.WARN)) return;

    const entry = this.formatEntry(LogLevel.WARN, message, context);

    if (import.meta.env.DEV) {
      if (context) {
        console.warn(`[WARN] ${entry.timestamp}`, message, context);
      } else {
        console.warn(`[WARN] ${entry.timestamp}`, message);
      }
    }

    this.sendToService(entry).catch(() => { });
  }

  /**
   * Error logging
   */
  error(message: string, error?: Error | unknown, context?: Record<string, unknown>): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;

    const errorContext = error instanceof Error
      ? {
        error: error.message,
        stack: error.stack,
        name: error.name,
        ...context
      }
      : context;

    const entry = this.formatEntry(LogLevel.ERROR, message, errorContext);

    if (import.meta.env.DEV) {
      console.error(`[ERROR] ${entry.timestamp}`, message, error, context);
    }

    this.sendToService(entry).catch(() => { });
  }

  /**
   * Performance timing
   */
  time(label: string): void {
    if (import.meta.env.DEV) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (import.meta.env.DEV) {
      console.timeEnd(label);
    }
  }

  /**
   * Group logs together
   */
  group(label: string): void {
    if (import.meta.env.DEV) {
      console.group(label);
    }
  }

  groupEnd(): void {
    if (import.meta.env.DEV) {
      console.groupEnd();
    }
  }
}

export const logger = new Logger();

// Default to INFO level in production, DEBUG in development
if (import.meta.env.DEV) {
  logger.setLevel(LogLevel.DEBUG);
}

